import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, Heart, Sparkles, TrendingUp, Crown, CheckCircle, MapPin, Briefcase, GraduationCap, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card } from './ui/card';
import { ImageWithFallback } from './figma/ImageWithFallback';
import { useTheme, themes } from './ThemeProvider';
import { toast } from 'sonner';
import { API_URL } from '../lib/api-client';

interface SuggestedProfile {
  _id: string;
  name: string;
  age: number;
  photos: string[];
  location: string;
  profession: string;
  education: string;
  height: string;
  isPro?: boolean;
  isVerified?: boolean;
  matchScore?: number;
}

export function DailyInterestModal() {
  const [isOpen, setIsOpen] = useState(false);
  const [profiles, setProfiles] = useState<SuggestedProfile[]>([]);
  const [loading, setLoading] = useState(false);
  const [sendingInterest, setSendingInterest] = useState<string | null>(null);
  const [sentInterests, setSentInterests] = useState<Set<string>>(new Set());
  const { theme } = useTheme();
  const currentTheme = themes[theme];

  // Check and show modal based on day frequency
  useEffect(() => {
    checkAndShowModal();
  }, []);

  const checkAndShowModal = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      // Check if user has permanently disabled this modal
      const userDisabled = localStorage.getItem('dailyInterestModalDisabled');
      if (userDisabled === 'true') return;

      const lastShownDate = localStorage.getItem('dailyInterestModalLastShown');
      const today = new Date();
      
      // Fetch modal config from backend
      const axios = (await import('axios')).default;
      const configResponse = await axios.get(`${API_URL}/daily-interest-modal-config/config`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const config = configResponse.data?.data || {};
      const isEnabled = config.enabled !== false; // Default true
      const frequencyDays = config.frequencyDays || 3; // Default 3 days

      if (!isEnabled) return;

      // Check if enough days have passed
      if (lastShownDate) {
        const lastShown = new Date(lastShownDate);
        const daysSinceLastShown = Math.floor((today.getTime() - lastShown.getTime()) / (1000 * 60 * 60 * 24));
        
        if (daysSinceLastShown < frequencyDays) {
          return; // Not enough days passed
        }
      }

      // Fetch suggested profiles
      const profilesAvailable = await fetchSuggestedProfiles();
      
      // Update last shown date
      localStorage.setItem('dailyInterestModalLastShown', today.toISOString());
      
      // Show modal after a short delay
      if (profilesAvailable) {
        setTimeout(() => {
          setIsOpen(true);
        }, 2000);
      }
      
    } catch (error) {
      // Silently fail - modal is optional
    }
  };

  const fetchSuggestedProfiles = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) return;

      const axios = (await import('axios')).default;
      const response = await axios.get(`${API_URL}/matches/daily-suggestions`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { limit: 15 } // Get 10-15 profiles
      });

      if (response.data.success) {
        setProfiles(response.data.data.profiles || []);
        
        // If no profiles available, don't show modal
        if (!response.data.data.profiles || response.data.data.profiles.length === 0) {
          setIsOpen(false);
          return false;
        }
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error fetching suggested profiles:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendInterest = async (profileId: string) => {
    if (sendingInterest || sentInterests.has(profileId)) return;

    setSendingInterest(profileId);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Please login to send interest');
        return;
      }

      const axios = (await import('axios')).default;
      await axios.post(
        `${API_URL}/interests/send`,
        {
          recipientId: profileId,
          source: 'daily_suggestion_modal'
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // Mark as sent
      setSentInterests(prev => new Set([...prev, profileId]));
      toast.success('Interest sent successfully!');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to send interest');
    } finally {
      setSendingInterest(null);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleDontShowAgain = () => {
    localStorage.setItem('dailyInterestModalDisabled', 'true');
    toast.info('You won\'t see this modal again. You can re-enable it from settings.');
    setIsOpen(false);
  };

  return (
    <AnimatePresence mode="wait">
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
          onClick={handleClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className={`${currentTheme.gradient} p-6 text-white relative overflow-hidden`}>
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
                    >
                      <Sparkles className="size-8" />
                    </motion.div>
                    <div>
                      <h2 className="text-2xl font-bold">Daily Match Suggestions</h2>
                      <p className="text-white/90 text-sm">Connect with your perfect matches today!</p>
                    </div>
                  </div>
                  <button
                    onClick={handleClose}
                    className="p-2 hover:bg-white/20 rounded-full transition-colors"
                  >
                    <X className="size-6" />
                  </button>
                </div>
                
                <div className="flex items-center gap-2 mt-4">
                  <Badge className="bg-white/20 text-white border-0 backdrop-blur-sm">
                    <TrendingUp className="size-3 mr-1" />
                    {profiles.length} Profiles Available
                  </Badge>
                  <Badge className="bg-white/20 text-white border-0 backdrop-blur-sm">
                    <Heart className="size-3 mr-1" />
                    High Match Score
                  </Badge>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 max-h-[calc(90vh-200px)] overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <Loader2 className="size-12 animate-spin text-rose-500 mx-auto mb-4" />
                    <p className="text-gray-600">Loading amazing matches for you...</p>
                  </div>
                </div>
              ) : profiles.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Heart className="size-10 text-gray-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">No Suggestions Available</h3>
                  <p className="text-gray-600">Check back later for new matches!</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {profiles.map((profile, index) => {
                    const hasSentInterest = sentInterests.has(profile._id);
                    
                    return (
                      <motion.div
                        key={profile._id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        whileHover={{ y: -5 }}
                      >
                        <Card className="overflow-hidden border-0 shadow-lg hover:shadow-2xl transition-all">
                          {/* Profile Image */}
                          <div className="relative h-48">
                            <ImageWithFallback
                              src={profile.photos[0] || '/placeholder.jpg'}
                              alt={profile.name}
                              className="w-full h-full object-cover"
                            />
                            
                            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                            
                            {/* Badges */}
                            <div className="absolute top-2 left-2 right-2 flex items-start justify-between gap-1">
                              {profile.isVerified && (
                                <Badge className="bg-blue-500 border-0 text-white text-xs backdrop-blur-sm">
                                  <CheckCircle className="size-3 mr-1" />
                                  Verified
                                </Badge>
                              )}
                              {profile.isPro && (
                                <Badge className="bg-gradient-to-r from-amber-400 to-orange-500 border-0 text-white text-xs ml-auto">
                                  <Crown className="size-3 mr-1" />
                                  Pro
                                </Badge>
                              )}
                            </div>

                            {/* Match Score */}
                            {profile.matchScore && (
                              <motion.div
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                transition={{ delay: 0.3 + index * 0.05, type: "spring" }}
                                className="absolute top-2 right-2"
                              >
                                <div className="bg-green-500 text-white text-xs font-bold px-2 py-1 rounded-full backdrop-blur-sm shadow-lg">
                                  {profile.matchScore}% Match
                                </div>
                              </motion.div>
                            )}

                            {/* Name & Age */}
                            <div className="absolute bottom-2 left-2 right-2 text-white">
                              <h3 className="font-bold text-lg truncate">
                                {profile.name}, {profile.age}
                              </h3>
                              <p className="text-xs text-white/90 flex items-center gap-1 truncate">
                                <MapPin className="size-3 flex-shrink-0" />
                                {profile.location}
                              </p>
                            </div>
                          </div>

                          {/* Profile Details */}
                          <div className="p-3 space-y-2">
                            <div className="flex items-center gap-2 text-xs text-gray-600">
                              <Briefcase className="size-3 flex-shrink-0 text-blue-500" />
                              <span className="truncate">{profile.profession}</span>
                            </div>
                            <div className="flex items-center gap-2 text-xs text-gray-600">
                              <GraduationCap className="size-3 flex-shrink-0 text-purple-500" />
                              <span className="truncate">{profile.education}</span>
                            </div>

                            {/* Connect Button */}
                            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                              <Button
                                className={`w-full mt-2 ${
                                  hasSentInterest
                                    ? 'bg-green-500 hover:bg-green-600'
                                    : currentTheme.gradient
                                } text-white shadow-md hover:shadow-lg transition-all`}
                                onClick={() => handleSendInterest(profile._id)}
                                disabled={sendingInterest === profile._id || hasSentInterest}
                              >
                                {sendingInterest === profile._id ? (
                                  <>
                                    <Loader2 className="size-4 mr-2 animate-spin" />
                                    Sending...
                                  </>
                                ) : hasSentInterest ? (
                                  <>
                                    <CheckCircle className="size-4 mr-2" />
                                    Interest Sent
                                  </>
                                ) : (
                                  <>
                                    <Heart className="size-4 mr-2" />
                                    Connect Now
                                  </>
                                )}
                              </Button>
                            </motion.div>
                          </div>
                        </Card>
                      </motion.div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="border-t p-4 bg-gray-50">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
                <p className="text-sm text-gray-600">
                  <Sparkles className="size-4 inline mr-1 text-amber-500" />
                  Send interests now to increase your matches!
                </p>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={handleDontShowAgain} className="text-gray-600 hover:text-gray-800">
                    Don't show again
                  </Button>
                  <Button variant="outline" onClick={handleClose}>
                    Close
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}